// calculateIncreasingPayment.js - Revised
export function calculateIncreasingPayment(data) {
    const {
        orderAmount = "0",
        term = "0",
        rate = "0",
        periodInput = 1,
        startPercentage = 0.7,
        endPercentage = 1.3,
    } = data || {};

    // Parse input values
    const principal = parseFloat(orderAmount) || 0;
    const months = parseInt(term) || 0;
    const monthlyRate = parseFloat(rate) / 100 || 0;
    const period = parseInt(periodInput) || 1;
    const startFactor = parseFloat(startPercentage) || 0.7;
    const endFactor = parseFloat(endPercentage) || 1.3;

    if (principal <= 0 || months <= 0 || monthlyRate < 0) {
        return [];
    }

    // Calculate the number of periods and the distribution of payment factors
    const numPeriods = Math.ceil(months / period);
    const paymentDistribution = [];

    // Calculate the step size for factor increase
    const factorStep = numPeriods > 1 ? (endFactor - startFactor) / (numPeriods - 1) : 0;

    // Generate the distribution of factors for each month
    for (let i = 0; i < numPeriods; i++) {
        const periodFactor = startFactor + (factorStep * i);
        const periodMonths = (i === numPeriods - 1) ? months - (i * period) : period;

        for (let j = 0; j < periodMonths; j++) {
            paymentDistribution.push(periodFactor);
        }
    }

    // Zero interest case
    if (monthlyRate === 0) {
        // Calculate a base payment (will be adjusted by factors)
        const basePayment = principal / months;

        // First pass: calculate payments with factors
        let tempPayments = [];
        let totalPaymentSum = 0;

        for (let i = 0; i < months; i++) {
            const factorAdjustedPayment = basePayment * paymentDistribution[i];
            tempPayments.push({
                month: i + 1,
                factor: paymentDistribution[i],
                baseAmount: factorAdjustedPayment
            });
            totalPaymentSum += factorAdjustedPayment;
        }

        // Calculate scaling factor to ensure total equals principal
        const scalingFactor = principal / totalPaymentSum;

        // Second pass: apply scaling and generate final payment plan
        let remainingPrincipal = principal;
        let payments = [];

        for (let i = 0; i < months; i++) {
            // For the last payment, use remaining principal exactly
            let principalPayment;
            if (i === months - 1) {
                principalPayment = remainingPrincipal;
            } else {
                principalPayment = tempPayments[i].baseAmount * scalingFactor;
            }

            payments.push({
                month: i + 1,
                amount: principalPayment,
                principal: principalPayment,
                interest: 0,
                remainingPrincipal: i === months - 1 ? 0 : remainingPrincipal - principalPayment
            });

            remainingPrincipal -= principalPayment;
        }

        // Format and return
        return payments.map(payment => ({
            ...payment,
            amount: parseFloat(payment.amount.toFixed(2)),
            principal: parseFloat(payment.principal.toFixed(2)),
            interest: parseFloat(payment.interest.toFixed(2)),
            remainingPrincipal: parseFloat(payment.remainingPrincipal.toFixed(2)),
        }));
    }

    // With interest - standard calculation
    // Calculate equal monthly payment as reference
    const equalMonthlyPayment = (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -months));

    // First pass: calculate base payments with factors
    let basePayments = [];
    let cumulativePrincipal = 0;
    let remainingPrincipal = principal;

    for (let i = 0; i < months; i++) {
        const interest = remainingPrincipal * monthlyRate;
        const basePayment = equalMonthlyPayment * paymentDistribution[i];
        let principalPayment = basePayment - interest;

        // Ensure principal payment is not negative
        if (principalPayment < 0) {
            principalPayment = 0;
        }

        const actualPayment = interest + principalPayment;

        basePayments.push({
            month: i + 1,
            amount: actualPayment,
            principal: principalPayment,
            interest: interest,
            factor: paymentDistribution[i]
        });

        cumulativePrincipal += principalPayment;
        remainingPrincipal -= principalPayment;
    }

    // Calculate scaling factor for principal payments
    const scaleFactor = principal / Math.max(0.001, cumulativePrincipal);

    // Second pass: create final payment plan with scaled principal payments
    remainingPrincipal = principal;
    let payments = [];

    for (let i = 0; i < months; i++) {
        const interest = remainingPrincipal * monthlyRate;

        // For the last payment, use remaining principal exactly
        let principalPayment;
        if (i === months - 1) {
            principalPayment = remainingPrincipal;
        } else {
            principalPayment = basePayments[i].principal * scaleFactor;
            // Ensure we don't exceed remaining principal
            if (principalPayment > remainingPrincipal) {
                principalPayment = remainingPrincipal;
            }
        }

        const paymentAmount = interest + principalPayment;

        payments.push({
            month: i + 1,
            amount: paymentAmount,
            principal: principalPayment,
            interest: interest,
            remainingPrincipal: i === months - 1 ? 0 : remainingPrincipal - principalPayment
        });

        remainingPrincipal -= principalPayment;
    }

    // Format and return
    return payments.map(payment => ({
        ...payment,
        amount: parseFloat(payment.amount.toFixed(2)),
        principal: parseFloat(payment.principal.toFixed(2)),
        interest: parseFloat(payment.interest.toFixed(2)),
        remainingPrincipal: parseFloat(payment.remainingPrincipal.toFixed(2)),
    }));
}